import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";
import { ECampaignPagesError } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";

const MAX_URL_LENGTH = 2048;
export const MAX_CAMPAIGN_NAME_LENGTH = 200;
export const MIN_CAMPAIGN_NAME_LENGTH = 2;

const wizardBrandMonitoringErrorMessagesMapping = {
    [ECampaignPagesError.MAX_LENGTH]:
        "paid.brandMonitoringCampaign.wizard.selectSiteStep.projectName.input.error.maxLength",
    [ECampaignPagesError.MINIMUM_LENGTH]:
        "paid.brandMonitoringCampaign.wizard.selectSiteStep.projectName.input.error.minimumLength",
    [ECampaignPagesError.DUPLICATE_NAME]: "bmc.wizard.selectSiteStep.selectedSite.input.error.duplicateProjectName",
    [ECampaignPagesError.EMPTY_FIELD]:
        "paid.brandMonitoringCampaign.wizard.selectSiteStep.selectedSite.input.error.emptyField",
    [ECampaignPagesError.INVALID_TEXT]:
        "paid.brandMonitoringCampaign.wizard.selectSiteStep.selectedSite.input.error.invalidUrl",
    [ECampaignPagesError.SITE_MAX_LENGTH]:
        "paid.brandMonitoringCampaign.wizard.selectSiteStep.selectedSite.input.error.maxLength",
    [ECampaignPagesError.INVALID_CAMPAIGN_NAME]:
        "seo.keywordsCampaign.wizard.selectSiteStep.projectName.input.error.invalidProjectName",
    [ECampaignPagesError.INVALID_CAMPAIGN_NAME_BOUNDARIES]:
        "seo.keywordsCampaign.wizard.selectSiteStep.projectName.input.error.invalidProjectNameBoundaries",
};

export const getWizardBrandMonitoringErrorMsg = (errorAsEnum: ECampaignPagesError): string => {
    return wizardBrandMonitoringErrorMessagesMapping[errorAsEnum] || "";
};

/**
 * Extracts the domain name from a given URL
 * The function will return the domain name if the URL is valid, otherwise it will return an empty string
 * @param {string} url The URL to extract the domain name from
 * @returns {string} The extracted domain name
 */
const getDomainFromUrl = (url: string): string => {
    // The regex pattern will match the domain name in a URL
    // It will return an array with the following structure:
    // [full match, protocol (http:// or https://), www. (if present), domain name, path (if present)]
    const domain = url.match(/^(.*:\/\/)?(www\.)?([.\-_a-zA-Z0-9]*)(\/?).*$/);
    // If the regex pattern matched, return the domain name
    // Otherwise, return an empty string
    return domain ? (domain?.[3] ?? "") : "";
};

/**
 * Checks if the given URL is valid and has a valid domain structure
 * This function will return true if the URL:
 *   1. Has a valid protocol (http or https)
 *   2. Has a valid domain name (not an empty string)
 *   3. Has a valid TLD (at least 2 characters after the last dot)
 *   Otherwise, it will return false
 * @param {string} urlString The URL to validate
 * @returns {boolean} True if the URL is valid, false otherwise
 */
const isValidUrl = (urlString: string): boolean => {
    try {
        // Remove protocol and query params
        const validatedURL = urlString
            .toLowerCase()
            ?.trim()
            ?.replace("http://", "")
            ?.replace("https://", "")
            ?.split("?")?.[0];
        // Extract domain
        const domain = getDomainFromUrl(validatedURL);
        // Verify domain has valid TLD (at least 2 chars after last dot)
        const lastIndexOfDot = domain.lastIndexOf(".");
        return lastIndexOfDot > 0 && domain.slice(lastIndexOfDot)?.length > 2;
    } catch (error) {
        // If any of the above fails, return false
        return false;
    }
};

const getInputErrorSelectedSite = (site: IAsset): ECampaignPagesError => {
    if (!site) {
        return ECampaignPagesError.EMPTY_FIELD;
    }
    const url = site.displayText;
    if (url?.length === 0) {
        return ECampaignPagesError.EMPTY_FIELD;
    } else if (url.length > MAX_URL_LENGTH) {
        return ECampaignPagesError.SITE_MAX_LENGTH;
    } else if (!isValidUrl(url)) {
        return ECampaignPagesError.INVALID_TEXT;
    }
    return null;
};

export const isDuplicate = (projectName: string, campaignsList: string[]): boolean =>
    campaignsList?.some((campaign): boolean => campaign === projectName);

export const getSiteURLError = (site: IAsset): ECampaignPagesError => {
    const error = getInputErrorSelectedSite(site);
    return error;
};

const getMaxLengthSelectedSiteError = (siteName: string): ECampaignPagesError => {
    const error = siteName.length > MAX_URL_LENGTH ? ECampaignPagesError.SITE_MAX_LENGTH : null;
    return error;
};
